import { Instance, SnapshotOut, types } from 'mobx-state-tree';
import { CustomerApi } from 'src/services/customer/Customer';
import {
  TDeleteCustomerResult,
  TGetCustomerResult,
  TGetCustomersResult,
  TPostCustomerResult,
  TUpdateCustomerResult,
} from 'src/services/customer/CustomerTypes';
import { PartnerApi } from 'src/services/partner/Partner';
import {
  TDeletePartnerResult,
  TGetPartnerResult,
  TGetPartnersResult,
  TPostPartnerResult,
  TUpdatePartnerResult,
} from 'src/services/partner/PartnerTypes';

import {
  createCustomer,
  Customer,
  ICustomer,
  ICustomerFilter,
  ICustomerSnapshot,
} from '../customer/Customer';
import { withEnvironment } from '../extensions/with-environment';
import { withRootStore } from '../extensions/with-root-store';
import { createPagination, IPagination, IPaginationSnapshot } from '../pagination/Pagination';
import {
  createPartner,
  IPartner,
  IPartnerFilter,
  IPartnerSnapshot,
  Partner,
} from '../partner/Partner';

/**
 * # CrmStore
 *
 * CrmStore을 설명하세요.
 */
export const CrmStore = types
  .model('CrmStore')
  // --------------------------------------------------------------------------
  .props({
    pagination: createPagination(),
    customers: types.optional(types.array(Customer), []),
    customer: createCustomer(),
    partners: types.optional(types.array(Partner), []),
    partner: createPartner(),
  })
  .extend(withEnvironment)
  .extend(withRootStore)
  // eslint-disable-line @typescript-eslint/no-unused-vars
  .views((self) => ({}))
  // --------------------------------------------------------------------------
  // MUTATEs - 모델 상태를 변경
  .actions((self) => ({
    setPagination: (pagination: IPaginationSnapshot) => {
      self.pagination = pagination as IPagination;
    },
    resetPagination: () => {
      self.pagination = { size: 25, page: 1 } as IPagination;
    },
    setCustomers: (customers: ICustomer[]) => {
      self.customers.replace(customers);
    },
    setCustomer: (customer: ICustomerSnapshot) => {
      self.customer = customer as ICustomer;
    },
    setPartners: (partners: IPartner[]) => {
      self.partners.replace(partners);
    },
    setPartner: (partner: IPartnerSnapshot) => {
      self.partner = partner as IPartner;
    },
    resetCustomer: () => {
      self.customer = {} as ICustomer;
    },
    resetPartner: () => {
      self.partner = {} as IPartner;
    },
  }))
  // --------------------------------------------------------------------------
  // REQUESTs - 서비스 요청 및 기타 인터페이스 요청
  .actions((self) => ({
    /**
     * 고객사
     */

    getCustomers: async (filter?: ICustomerFilter) => {
      const customerApi: CustomerApi = new CustomerApi(self.environment.api);
      const result: TGetCustomersResult = await customerApi.gets(self.pagination, filter);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setCustomers(result.customers);
        result.pagination && self.setPagination(result.pagination);
      } else {
        console.error(result.kind);
      }
    },
    appendCustomers: async (filter?: ICustomerFilter) => {
      try {
        const customerApi: CustomerApi = new CustomerApi(self.environment.api);
        const result: TGetCustomersResult = await customerApi.gets(self.pagination, filter);
        if (self.rootStore.responseStore.getResponseResult(result)) {
          if (result.kind === 'ok') {
            self.setCustomers(self.customers.concat(result.customers));
            result.pagination && self.setPagination(result.pagination);
          }
        }
      } catch (error) {
        self.rootStore.responseStore.errorProcessing(error);
      }
    },
    getCustomer: async (customerSid: number) => {
      const customerApi: CustomerApi = new CustomerApi(self.environment.api);
      const result: TGetCustomerResult = await customerApi.get(customerSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setCustomer(result.customer);
      } else {
        console.error(result.kind);
      }
    },
    postCustomer: async (customer: ICustomerSnapshot) => {
      const customerApi: CustomerApi = new CustomerApi(self.environment.api);
      const result: TPostCustomerResult = await customerApi.post(customer);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setCustomer(result.customer);
      } else {
        console.error(result.kind);
      }
    },
    updateCustomer: async (customerSid: number, customer: ICustomerSnapshot) => {
      const customerApi: CustomerApi = new CustomerApi(self.environment.api);
      const result: TUpdateCustomerResult = await customerApi.update(customerSid, customer);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setCustomer(result.customer);
      } else {
        console.error(result.kind);
      }
    },
    deleteCustomer: async (customerSid: number) => {
      const customerApi: CustomerApi = new CustomerApi(self.environment.api);
      const result: TDeleteCustomerResult = await customerApi.delete(customerSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        // self.setCustomer(result.customer);
      } else {
        console.error(result.kind);
      }
    },

    // --------------------------------------------------------------------------
    /**
     * 파트너사
     */
    getPartners: async (filter?: IPartnerFilter) => {
      const partnerApi: PartnerApi = new PartnerApi(self.environment.api);
      const result: TGetPartnersResult = await partnerApi.gets(self.pagination, filter);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setPartners(result.partners);
        result.pagination && self.setPagination(result.pagination);
      } else {
        console.error(result.kind);
      }
    },
    getPartner: async (partnerSid: number) => {
      const partnerApi: PartnerApi = new PartnerApi(self.environment.api);
      const result: TGetPartnerResult = await partnerApi.get(partnerSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setPartner(result.partner);
      } else {
        console.error(result.kind);
      }
    },
    postPartner: async (partner: IPartnerSnapshot) => {
      const partnerApi: PartnerApi = new PartnerApi(self.environment.api);
      const result: TPostPartnerResult = await partnerApi.post(partner);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setPartner(result.partner);
      } else {
        console.error(result.kind);
      }
    },
    updatePartner: async (partnerSid: number, partner: IPartnerSnapshot) => {
      const partnerApi: PartnerApi = new PartnerApi(self.environment.api);
      const result: TUpdatePartnerResult = await partnerApi.update(partnerSid, partner);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setPartner(result.partner);
      } else {
        console.error(result.kind);
      }
    },
    deletePartner: async (partnerSid: number) => {
      const partnerApi: PartnerApi = new PartnerApi(self.environment.api);
      const result: TDeletePartnerResult = await partnerApi.delete(partnerSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        // self.setPartner(result.partner);
      } else {
        console.error(result.kind);
      }
    },
  }));

// --------------------------------------------------------------------------
type TCrmStore = Instance<typeof CrmStore>;
type TCrmStoreSnapshot = SnapshotOut<typeof CrmStore>;

export interface ICrmStore extends TCrmStore {}
export type TCrmStoreKeys = keyof TCrmStoreSnapshot & string;
export interface ICrmStoreSnapshot extends TCrmStoreSnapshot {}
export const createCrmStore = () => types.optional(CrmStore, {} as TCrmStore);
